import { useMemo } from 'react';
import { MeasurementMarkerDto, SessionOptionDto } from 'types/models/SessionsPageData';
import ChartHint from './ChartHint';
import { t } from 'utilities/TranslationUtils';
import { darkTooltip, glassBucketBarStyle, calculatePercentYAxisRange } from 'utilities/chartUtils';
import { buildRestGapBucketData } from './playerPerformanceUtils';
import GrowChart from './GrowChart';

interface RestGapGoalRateChartProps {
  measurements: MeasurementMarkerDto[];
  sessions: SessionOptionDto[];
}

interface BucketDef {
  labelKey: string;
  min: number;
  max: number;
}

const BUCKETS: BucketDef[] = [
  { labelKey: 'playerPerformance.bucket_lte7days',    min: 0,  max: 7        },
  { labelKey: 'playerPerformance.bucket_8_14days',    min: 8,  max: 14       },
  { labelKey: 'playerPerformance.bucket_15_30days',   min: 15, max: 30       },
  { labelKey: 'playerPerformance.bucket_30plusdays',  min: 31, max: Infinity },
];


const RestGapGoalRateChart: React.FunctionComponent<RestGapGoalRateChartProps> = ({
  measurements, sessions,
}) => {
  const buckets = useMemo(
    () => BUCKETS.map((b) => ({ label: t(b.labelKey), min: b.min, max: b.max })),
    [],
  );

  const bucketData = useMemo(
    () => buildRestGapBucketData(measurements, sessions, buckets),
    [measurements, sessions, buckets],
  );

  const sessionCount = useMemo(() => {
    const ids = new Set<string>();
    for (const m of measurements) ids.add(m.sessionId);
    return ids.size;
  }, [measurements]);

  const option = useMemo(
    () => {
      const yAxisRange = calculatePercentYAxisRange(bucketData.map((b) => b.onTargetRate));
      return {
      tooltip: {
        ...darkTooltip('item'),
        formatter: (p: { name: string; value: number; dataIndex: number }) => {
          const pt = bucketData[p.dataIndex];
          if (pt.count === 0) return `<b>${pt.label}</b><br/>${t('playerPerformance.noSessionsInRange')}`;
          return [
            `<b>${pt.label}</b>`,
            `${t('sessions.fieldSessionsLabel')}: ${pt.count}  ·  ${t('playerPerformance.totalKicks')}: ${pt.kicks}`,
            `${t('playerPerformance.onTargetRate')}: ${p.value.toFixed(1)}%`,
          ].join('<br/>');
        },
      },
      grid: { top: 30, bottom: 36, left: 48, right: 16, containLabel: false },
      xAxis: {
        type: 'category' as const,
        data: bucketData.map((b) => b.label),
        axisLabel: { fontSize: 12, color: '#374151' },
        axisLine: { lineStyle: { color: '#E5E7EB' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        min: yAxisRange.min,
        max: yAxisRange.max,
        axisLabel: { fontSize: 12, color: '#374151', formatter: (v: number) => `${Math.min(100, v).toFixed(0)}%` },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar' as const,
          data: bucketData.map((b) => ({
            value: b.count === 0 ? 0 : b.onTargetRate,
            itemStyle: glassBucketBarStyle(),
          })),
          label: {
            show: true,
            position: 'top' as const,
            formatter: (p: { value: number; dataIndex: number }) => {
              const bucket = bucketData[p.dataIndex];
              return bucket.count === 0 ? t('playerPerformance.na') : `${p.value.toFixed(1)}%`;
            },
            fontSize: 13,
            fontWeight: 600,
            color: '#111827',
          },
        },
      ],
      };
    },
    [bucketData],
  );

  if (sessionCount < 2) return null;

  return (
    <div className='pp-section'>
      <h4>{t('playerPerformance.onTargetRateRestGapChartTitle')} <ChartHint hintKey='playerPerformance.chartHint_restGap' /></h4>
      <GrowChart option={option} />
    </div>
  );
};

export default RestGapGoalRateChart;
